import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, map, shareReplay, tap } from 'rxjs';
import { config } from '../config';
import {
  ADD_ON_CONTROLLED_SETTINGS,
  AddOnApplicabilitySource,
  AddOnAssignmentMode,
  CompanyAddOnAssignmentPayload,
  CompanyAddOnCatalogItem,
  AddOnControlledSetting,
  AddOnEntitlementConfig,
  AddOnKey,
  CompanyAddOnEntitlement,
  CompanyAddOnEntitlementsResponse,
  CompanySettingsEntitlementsResponse,
  EffectiveAddOnEntitlement,
  EffectiveAddOnEntitlements,
  EmployeeSettingsEntitlementRow,
  SettingsFieldEntitlement,
  SettingsFieldEntitlementMap,
  SettingsScope,
} from '../entitlements/settings-entitlements.types';

@Injectable({
  providedIn: 'root',
})
export class SettingsEntitlementsService {
  readonly controlledSettings = ADD_ON_CONTROLLED_SETTINGS;
  private readonly assignmentCacheTtlMs = 30_000;
  private companyAssignmentCache = new Map<number, {
    expiresAt: number;
    response$: Observable<CompanyAddOnEntitlementsResponse>;
  }>();

  constructor(private http: HttpClient) {}

  getCompanySettingsEntitlements(companyId: string | number): Observable<CompanySettingsEntitlementsResponse> {
    return this.getCompanyAddOnEntitlements(companyId)
      .pipe(map((response) => this.buildCompanySettingsEntitlements(companyId, response)));
  }

  getCompanyAddOnEntitlements(companyId: string | number): Observable<CompanyAddOnEntitlementsResponse> {
    return forkJoin({
      catalog: this.getCompanyAddOnCatalog(),
      assignments: this.getCompanyAssignedAddOns(companyId),
    }).pipe(
      map(({ catalog, assignments }) => this.normalizeCompanyAddOnResponse(companyId, assignments, catalog))
    );
  }

  /** Read current company assignments without loading the catalog. */
  getCompanyAssignedAddOns(companyId: string | number): Observable<CompanyAddOnEntitlementsResponse> {
    const normalizedCompanyId = Number(companyId);
    const cached = this.companyAssignmentCache.get(normalizedCompanyId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.response$;
    }

    const response$ = this.http
      .get<CompanyAddOnEntitlementsResponse>(
        `${config.baseUrl}supersee/v1/admin/company-addons?company_id=${companyId}`
      )
      .pipe(
        map((response) => this.normalizeCompanyAddOnResponse(companyId, response)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    if (Number.isFinite(normalizedCompanyId) && normalizedCompanyId > 0) {
      this.companyAssignmentCache.set(normalizedCompanyId, {
        expiresAt: Date.now() + this.assignmentCacheTtlMs,
        response$,
      });
    }

    return response$;
  }

  getCompanyAddOnCatalog(): Observable<CompanyAddOnCatalogItem[]> {
    return this.http
      .get<{ addons?: CompanyAddOnCatalogItem[] }>(
        `${config.baseUrl}supersee/v1/admin/company-addons/catalog`
      )
      .pipe(map((response) => this.normalizeCatalog(response?.addons)));
  }

  updateCompanyAddOn(payload: CompanyAddOnAssignmentPayload): Observable<CompanyAddOnEntitlementsResponse> {
    return this.http
      .post<CompanyAddOnEntitlementsResponse>(
        `${config.baseUrl}supersee/v1/admin/company-addons`,
        payload
      )
      .pipe(
        tap(() => this.companyAssignmentCache.delete(Number(payload.company_id))),
        map((response) => this.normalizeCompanyAddOnResponse(payload.company_id, response))
      );
  }

  buildEmployeeEffectiveEntitlements(
    companyAddOns: CompanyAddOnEntitlement[],
    userId: string | number
  ): EffectiveAddOnEntitlements {
    const id = Number(userId);
    return companyAddOns.reduce((entitlements, addon) => {
      const appliesToUser =
        addon.enabled
        && (
          addon.assignment_mode === 'company'
          || addon.assignment_mode === 'all_users'
          || (
            addon.assignment_mode === 'specific_users'
            && addon.user_ids.includes(id)
          )
        );

      entitlements[addon.key] = {
        enabled: appliesToUser,
        source: appliesToUser ? addon.assignment_mode : null,
        editable: appliesToUser,
        config: addon.config ?? {},
      };

      return entitlements;
    }, {} as EffectiveAddOnEntitlements);
  }

  getControlledSetting(field: string): AddOnControlledSetting | undefined {
    return this.controlledSettings.find((setting) => setting.field === field);
  }

  getEmployeeFieldEntitlement(
    employee: EmployeeSettingsEntitlementRow | null | undefined,
    field: string
  ): SettingsFieldEntitlement | null {
    if (!employee) {
      return null;
    }

    if (employee.field_entitlements?.[field]) {
      return employee.field_entitlements[field];
    }

    const controlledSetting = this.getControlledSetting(field);
    if (!controlledSetting) {
      return null;
    }

    return this.resolveEffectiveFieldEntitlement(
      field,
      controlledSetting.addonKey,
      employee.addons?.[controlledSetting.addonKey],
      'employee'
    );
  }

  canEditEmployeeSetting(
    employee: EmployeeSettingsEntitlementRow | null | undefined,
    field: string
  ): boolean {
    const fieldEntitlement = this.getEmployeeFieldEntitlement(employee, field);
    return fieldEntitlement ? fieldEntitlement.editable : true;
  }

  getAddOnLimitMinutes(addon: CompanyAddOnEntitlement | null | undefined): number | null {
    const raw =
      addon?.config?.['limit_minutes']
      ?? (addon as unknown as Record<string, unknown> | null | undefined)?.['limit_minutes'];
    const minutes = Number(raw);
    return Number.isFinite(minutes) && minutes >= 1 ? Math.floor(minutes) : null;
  }

  private normalizeCompanySettingsEntitlements(
    response: CompanySettingsEntitlementsResponse
  ): CompanySettingsEntitlementsResponse {
    const companyAddons = this.normalizeCompanyAddOns(response?.company_addons);
    const employees = Array.isArray(response?.employees)
      ? response.employees.map((employee) => this.normalizeEmployeeRow(employee))
      : [];

    return {
      company_id: Number(response?.company_id ?? 0),
      company_settings: response?.company_settings ?? {},
      company_addons: companyAddons,
      company_field_entitlements:
        response?.company_field_entitlements
        ?? this.buildCompanyFieldEntitlements(companyAddons),
      employees,
    };
  }

  private buildCompanySettingsEntitlements(
    companyId: string | number,
    response: CompanyAddOnEntitlementsResponse
  ): CompanySettingsEntitlementsResponse {
    const companyAddons = this.normalizeCompanyAddOns(response?.assigned_addons ?? response?.addons);

    return {
      company_id: Number(response?.company_id ?? companyId),
      company_settings: {},
      company_addons: companyAddons,
      all_users_addons: companyAddons.filter((addon) => addon.assignment_mode === 'all_users'),
      specific_users_addons: companyAddons.filter((addon) => addon.assignment_mode === 'specific_users'),
      addon_limits: response?.limits ?? {},
      company_field_entitlements: this.buildCompanyFieldEntitlements(companyAddons),
      employees: [],
    };
  }

  private normalizeEmployeeRow(employee: EmployeeSettingsEntitlementRow): EmployeeSettingsEntitlementRow {
    const addons = this.normalizeEffectiveAddOns(employee?.addons);

    return {
      user_id: Number(employee?.user_id ?? 0),
      name: employee?.name ?? '',
      email: employee?.email ?? '',
      personalized_settings: this.normalizeBoolean(employee?.personalized_settings),
      settings: employee?.settings ?? {},
      addons,
      field_entitlements: employee?.field_entitlements ?? this.buildEmployeeFieldEntitlements(addons),
    };
  }

  private normalizeCompanyAddOns(addons: CompanyAddOnEntitlement[] | undefined): CompanyAddOnEntitlement[] {
    if (!Array.isArray(addons)) {
      return [];
    }

    return addons.map((addon) => ({
      key: String(addon?.key ?? ''),
      label: addon?.label || String(addon?.key ?? '').replace(/_/g, ' '),
      enabled: this.normalizeBoolean(addon?.enabled),
      assignment_mode: this.normalizeAssignmentMode(addon?.assignment_mode),
      user_ids: Array.isArray(addon?.user_ids)
        ? addon.user_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [],
      config: this.normalizeConfig(addon?.config),
      revision: addon?.revision === undefined ? undefined : Number(addon.revision),
      assignment_id: addon?.assignment_id === undefined ? undefined : Number(addon.assignment_id),
      status: addon?.status ?? '',
      description: addon?.description ?? '',
      assignment_modes: this.normalizeAssignmentModes(addon?.assignment_modes),
      effective_enabled: this.normalizeBoolean(addon?.effective_enabled ?? addon?.enabled),
      effective_from: addon?.effective_from ?? null,
      effective_until: addon?.effective_until ?? null,
      expired: this.normalizeBoolean(addon?.expired),
    }));
  }

  private normalizeCompanyAddOnResponse(
    companyId: string | number,
    response: CompanyAddOnEntitlementsResponse | undefined,
    catalog: CompanyAddOnCatalogItem[] = []
  ): CompanyAddOnEntitlementsResponse {
    const rawAssigned = response?.assigned_addons
      ?? response?.addons
      ?? (response?.addons_by_key ? Object.values(response.addons_by_key) : []);
    const assignedAddons = this.normalizeCompanyAddOns(rawAssigned);
    const availableAddons = this.normalizeCatalog(response?.available_addons ?? catalog);
    const assignedByKey = assignedAddons.reduce((map, addon) => {
      map[String(addon.key)] = addon;
      return map;
    }, {} as Record<string, CompanyAddOnEntitlement>);

    return {
      company_id: Number(response?.company_id ?? companyId),
      available_addons: availableAddons,
      assigned_addons: assignedAddons,
      addons_by_key: assignedByKey,
      enabled_addon_keys: Array.isArray(response?.enabled_addon_keys)
        ? response.enabled_addon_keys.map((key) => String(key))
        : assignedAddons.filter((addon) => addon.enabled).map((addon) => String(addon.key)),
      effective_company_entitlements: Array.isArray(response?.effective_company_entitlements)
        ? response.effective_company_entitlements.map((key) => String(key))
        : [],
      limits: this.normalizeConfig(response?.limits),
      addons: assignedAddons,
    };
  }

  private normalizeCatalog(addons: CompanyAddOnCatalogItem[] | undefined): CompanyAddOnCatalogItem[] {
    if (!Array.isArray(addons)) {
      return [];
    }

    return addons.map((addon) => ({
      key: String(addon?.key ?? ''),
      addon_type: addon?.addon_type ?? '',
      label: addon?.label || String(addon?.key ?? '').replace(/_/g, ' '),
      description: addon?.description ?? '',
      assignment_modes: this.normalizeAssignmentModes(addon?.assignment_modes),
      effect: addon?.effect ?? {},
    }));
  }

  private normalizeEffectiveAddOns(addons: EffectiveAddOnEntitlements | undefined): EffectiveAddOnEntitlements {
    if (!addons || typeof addons !== 'object') {
      return {};
    }

    return Object.keys(addons).reduce((normalized, key) => {
      const addon = addons[key];
      normalized[key] = {
        enabled: this.normalizeBoolean(addon?.enabled),
        source: this.normalizeSource(addon?.source),
        editable: this.normalizeBoolean(addon?.editable),
        config: this.normalizeConfig(addon?.config),
      };
      return normalized;
    }, {} as EffectiveAddOnEntitlements);
  }

  private buildCompanyFieldEntitlements(addons: CompanyAddOnEntitlement[]): SettingsFieldEntitlementMap {
    return this.controlledSettings.reduce((fieldMap, setting) => {
      if (!setting.scopes.includes('company')) {
        return fieldMap;
      }

      const addon = addons.find((item) => item.key === setting.addonKey);
      const enabled = Boolean(addon?.enabled);
      const editable = enabled && (addon?.assignment_mode === 'company' || addon?.assignment_mode === 'all_users');

      fieldMap[setting.field] = {
        field: setting.field,
        addon_key: setting.addonKey,
        enabled,
        editable,
        source: enabled ? addon?.assignment_mode ?? null : null,
        config: addon?.config ?? {},
        reason: editable ? undefined : `${setting.label} is not enabled company-wide.`,
      };
      return fieldMap;
    }, {} as SettingsFieldEntitlementMap);
  }

  private buildEmployeeFieldEntitlements(addons: EffectiveAddOnEntitlements): SettingsFieldEntitlementMap {
    return this.controlledSettings.reduce((fieldMap, setting) => {
      if (!setting.scopes.includes('employee')) {
        return fieldMap;
      }

      fieldMap[setting.field] = this.resolveEffectiveFieldEntitlement(
        setting.field,
        setting.addonKey,
        addons[setting.addonKey],
        'employee'
      );
      return fieldMap;
    }, {} as SettingsFieldEntitlementMap);
  }

  private resolveEffectiveFieldEntitlement(
    field: string,
    addonKey: AddOnKey,
    addon: EffectiveAddOnEntitlement | undefined,
    scope: SettingsScope
  ): SettingsFieldEntitlement {
    const label = this.controlledSettings.find((setting) => setting.field === field)?.label ?? addonKey;
    const enabled = Boolean(addon?.enabled);
    const editable = Boolean(addon?.editable);

    return {
      field,
      addon_key: addonKey,
      enabled,
      editable,
      source: enabled ? addon?.source ?? null : null,
      config: addon?.config ?? {},
      reason: editable ? undefined : `${label} is not assigned to this ${scope}.`,
    };
  }

  private normalizeAssignmentMode(mode: unknown): 'company' | 'all_users' | 'specific_users' {
    return mode === 'company' || mode === 'all_users' || mode === 'specific_users'
      ? mode
      : 'company';
  }

  private normalizeAssignmentModes(modes: unknown): AddOnAssignmentMode[] {
    if (!Array.isArray(modes)) {
      return [];
    }

    return modes.filter((mode): mode is AddOnAssignmentMode =>
      mode === 'company' || mode === 'all_users' || mode === 'specific_users'
    );
  }

  private normalizeSource(source: unknown): AddOnApplicabilitySource {
    return source === 'company' || source === 'all_users' || source === 'specific_users'
      ? source
      : null;
  }

  private normalizeBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      return ['1', 'true', 'yes', 'enabled', 'active'].includes(value.trim().toLowerCase());
    }
    return false;
  }

  private normalizeConfig(configValue: unknown): AddOnEntitlementConfig {
    if (typeof configValue === 'string') {
      try {
        const parsed = JSON.parse(configValue);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed as AddOnEntitlementConfig
          : {};
      } catch {
        return {};
      }
    }

    return configValue && typeof configValue === 'object' && !Array.isArray(configValue)
      ? configValue as AddOnEntitlementConfig
      : {};
  }
}
